import { useMemo, useState } from "react";
import { NavLink, useLocation, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, FileText, Captions, X } from "lucide-react";
import { api, type Video } from "../lib/api";
import { SectionNav } from "./SectionNav";

type FilterKey = "withDigest" | "noDigest" | "withTranscript" | "noTranscript";

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: "withDigest", label: "Has digest" },
  { key: "noDigest", label: "No digest" },
  { key: "withTranscript", label: "Has transcript" },
  { key: "noTranscript", label: "No transcript" },
];

interface SidebarProps {
  /** Drawer-open state on mobile (< md). Ignored on md+ where the sidebar is always in-flow. */
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Record<FilterKey, boolean>>({
    withDigest: true,
    noDigest: true,
    withTranscript: true,
    noTranscript: true,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["videos"],
    queryFn: api.videos,
  });

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.filter((v) => {
      if (q && !v.title.toLowerCase().includes(q)) return false;
      const hasDigest = !!v.lastDigestAt;
      const hasTranscript = v.transcriptStatus === "complete";
      if (hasDigest && !filters.withDigest) return false;
      if (!hasDigest && !filters.noDigest) return false;
      if (hasTranscript && !filters.withTranscript) return false;
      if (!hasTranscript && !filters.noTranscript) return false;
      return true;
    });
  }, [data, search, filters]);

  return (
    <>
      {/* Mobile backdrop — only visible when drawer is open and below md. */}
      {mobileOpen && (
        <div
          className="fixed inset-0 top-14 z-20 bg-black/40 md:hidden"
          onClick={onMobileClose}
          aria-hidden
        />
      )}

      <aside
        className={`
          fixed top-14 bottom-0 left-0 z-30 flex w-72 flex-col overflow-hidden border-r border-rule bg-surface
          transition-transform duration-200 ease-out
          md:static md:z-auto md:w-auto md:translate-x-0
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        <div className="flex items-start justify-between border-b border-rule px-4 py-3">
          <div className="flex-1">
            {/* Mobile only — top-level module switcher, folded into the burger
                drawer (hidden on md+ where the topbar carries it). */}
            <div className="-mx-1 mb-3 border-b border-rule pb-3 md:hidden">
              <SectionNav orientation="vertical" onNavigate={onMobileClose} />
            </div>

            {/*
              Phase 6d page nav within the YouTube section. Comments dashboard
              is the second top-level YouTube view alongside Overview. Video
              detail isn't in this nav — it's reached by clicking a video row.
            */}
            <div className="mb-3 flex flex-wrap gap-1 text-[12px] font-medium">
              <PageNavLink to="/youtube" label="Overview" />
              <PageNavLink to="/youtube/comments" label="Comments" />
              <PageNavLink to="/youtube/themes" label="Themes" />
              <PageNavLink to="/youtube/methods" label="How it works" />
              {/* Agent 2 — salience eval gold marking (S2b). Standalone until the
                  full /salience section lands at S5. */}
              <PageNavLink to="/salience/eval" label="Salience eval" />
            </div>

            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
              Videos
            </div>

            <div className="relative">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted"
              />
              <input
                type="search"
                placeholder="Search titles…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded border border-rule bg-paper py-1.5 pl-8 pr-2 text-sm text-ink placeholder:text-ink-muted focus:border-brand-indigo focus:bg-surface focus:outline-none"
              />
            </div>

            <div className="mt-2.5 flex flex-wrap gap-1">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() =>
                    setFilters((s) => ({ ...s, [f.key]: !s[f.key] }))
                  }
                  className={`rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${
                    filters[f.key]
                      ? "border-rule bg-paper text-ink"
                      : "border-transparent bg-transparent text-ink-muted/60 line-through"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={onMobileClose}
            className="ml-2 rounded p-1 text-ink-muted hover:bg-black/5 hover:text-ink md:hidden"
            aria-label="Close menu"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="space-y-1 p-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-12 animate-pulse rounded bg-black/5" />
              ))}
            </div>
          )}

          {error && (
            <div className="p-4 text-sm text-ink-muted">
              Couldn't load videos.
            </div>
          )}

          {data && (
            <>
              <div className="px-4 pt-3 pb-1 text-[11px] text-ink-muted">
                {filtered.length} of {data.length}
              </div>
              <ul>
                {filtered.map((v) => (
                  <SidebarVideoRow key={v.videoId} video={v} />
                ))}
              </ul>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

function SidebarVideoRow({ video }: { video: Video }) {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const date = formatPublishedAt(video.publishedAt);
  const hasDigest = !!video.lastDigestAt;
  const hasTranscript = video.transcriptStatus === "complete";

  // Phase 6d: on the Comments dashboard, clicking a video adds ?videoId=...
  // to the dashboard URL rather than navigating to the video detail page.
  // Anywhere else, navigate to /youtube/videos/<id> as usual.
  const onDashboard = location.pathname === "/youtube/comments";
  const to = onDashboard
    ? `/youtube/comments?${buildDashboardParams(searchParams, video.videoId)}`
    : `/youtube/videos/${video.videoId}`;

  const activeOnDashboard =
    onDashboard && searchParams.get("videoId") === video.videoId;

  return (
    <li>
      <NavLink
        to={to}
        className={({ isActive }) => {
          const highlight = activeOnDashboard || (!onDashboard && isActive);
          return `block border-l-2 px-4 py-2.5 text-sm transition-colors ${
            highlight
              ? "border-brand-indigo bg-black/5 text-ink"
              : "border-transparent text-ink hover:bg-black/3"
          }`;
        }}
      >
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[11px] text-ink-muted">{date}</span>
          <span className="flex items-center gap-1.5">
            {hasTranscript && (
              <Captions
                size={11}
                className="text-ink-muted"
                aria-label="Transcript available"
              />
            )}
            {hasDigest && (
              <FileText
                size={11}
                className="text-ink-muted"
                aria-label="Digest available"
              />
            )}
          </span>
        </div>
        <div className="mt-0.5 line-clamp-2 leading-tight" title={video.title}>
          {video.title || video.videoId}
        </div>
      </NavLink>
    </li>
  );
}

function buildDashboardParams(
  current: URLSearchParams,
  videoId: string,
): string {
  const next = new URLSearchParams(current);
  if (next.get("videoId") === videoId) {
    next.delete("videoId");
  } else {
    next.set("videoId", videoId);
  }
  return next.toString();
}

function PageNavLink({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        `rounded px-2 py-1 transition-colors ${
          isActive
            ? "bg-brand-indigo/10 text-brand-indigo"
            : "text-ink-muted hover:bg-black/4 hover:text-ink"
        }`
      }
    >
      {label}
    </NavLink>
  );
}

function formatPublishedAt(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "2-digit",
  });
}
